import type { PopperProps } from '@mui/material';
import type { TooltipProps } from './types';

/**
 * Tooltip message가 실제로 표시 가능한지 확인합니다.
 */
const hasVisibleTooltipMessage = (message: TooltipProps['message']) =>
  message === 0 || (!!message && (!Array.isArray(message) || message.length > 0));

const getSxArray = (sx: PopperProps['sx']) => {
  if (!sx) {
    return [];
  }
  return Array.isArray(sx) ? sx : [sx];
};

const mergePopperSlotProps = (
  defaultSlotProps: Partial<PopperProps>,
  externalSlotProps?: Partial<PopperProps>,
) => {
  const { popperOptions, sx, ...otherExternalSlotProps } = externalSlotProps ?? {};
  const mergedSx = [...getSxArray(defaultSlotProps.sx), ...getSxArray(sx)];

  return {
    ...otherExternalSlotProps,
    sx: mergedSx.length > 0 ? mergedSx : undefined,
    popperOptions: {
      ...defaultSlotProps.popperOptions,
      ...popperOptions,
    },
  };
};

const resolvePopperSlotProps = (
  tooltipSlotProps: TooltipProps['slotProps'],
  defaultPopperSlotProps: Partial<PopperProps>,
) => {
  const externalPopperSlotProps = tooltipSlotProps?.popper;

  if (typeof externalPopperSlotProps !== 'function') {
    return mergePopperSlotProps(
      defaultPopperSlotProps,
      externalPopperSlotProps as Partial<PopperProps> | undefined,
    );
  }

  return (ownerState: Parameters<typeof externalPopperSlotProps>[0]) =>
    mergePopperSlotProps(
      defaultPopperSlotProps,
      externalPopperSlotProps({
        ...ownerState,
        ...defaultPopperSlotProps,
      } as Parameters<typeof externalPopperSlotProps>[0]) as Partial<PopperProps>,
    );
};

export { hasVisibleTooltipMessage, resolvePopperSlotProps };
